import { createSelector } from "reselect";
import type { RootState } from "single-spa/store/types";
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";
import type { CrmListObjectType, Integration } from "../../types";
import { CrmIntegrationListStatus } from "../../types";
import { selectHsIntegrationCrmStatus } from "./hubspotSelectors";
import { selectManagementReducer } from "./managementSelectors";
import { selectSfIntegrationCrmStatus } from "./salesforceSelectors";
import { swSettings } from "app/common/services/swSettings";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

const createCrmListsSelector = createStatePropertySelector(selectManagementReducer("crmLists"));

const selectCrmLists = createCrmListsSelector("lists");
export const selectCrmList = createSelector(
    [selectCrmLists, (_: RootState, props: [Integration, CrmListObjectType]) => props],
    (crmLists, [crmName, objectType]) => {
        return crmLists.find((list) => list.integrationName === crmName && list.objectType === objectType);
    },
);

const selectHasNonEmptyCrmList = createSelector(selectCrmLists, (crmLists) => {
    return crmLists.some((list) => list.syncedDomainsCount > 0);
});

const selectSfLists = createSelector(selectCrmLists, (crmLists) => {
    return crmLists.filter((list) => list.integrationName === "salesforce");
});

const selectAvailableSfLists = createSelector(
    selectHasNonEmptyCrmList,
    selectSfLists,
    (hasNonEmptyList, sfLists) => {
        return sfLists.filter((list) => !hasNonEmptyList || list.syncedDomainsCount > 0);
    },
);

const selectAvailableSfListObjectTypes = createSelector(selectAvailableSfLists, (sfLists) => {
    return sfLists.map((list) => list.objectType);
});

const selectHsLists = createSelector(selectCrmLists, (crmLists) => {
    return crmLists.filter((list) => list.integrationName === "hubspot");
});

const selectAvailableHsLists = createSelector(
    selectHasNonEmptyCrmList,
    selectHsLists,
    (hasNonEmptyList, hsLists) => {
        return hsLists.filter((list) => !hasNonEmptyList || list.syncedDomainsCount > 0);
    },
);

const selectAvailableHsListObjectTypes = createSelector(selectAvailableHsLists, (hsLists) => {
    return hsLists.map((list) => list.objectType);
});

export const selectAvailableCrmLists = createSelector(
    selectAvailableSfLists,
    selectAvailableHsLists,
    (sfLists, hsLists) => sfLists.concat(hsLists),
);

export const selectCrmListParams = createSelector(
    selectSfIntegrationCrmStatus,
    selectHsIntegrationCrmStatus,
    selectAvailableSfListObjectTypes,
    selectAvailableHsListObjectTypes,
    (sfStatus, hsStatus, sfObjectTypes, hsObjectTypes) => ({
        salesforce: { status: sfStatus, listObjectTypes: sfObjectTypes },
        hubspot: { status: hsStatus, listObjectTypes: hsObjectTypes },
    }),
);

export const selectSortedCRMs = createSelector(
    selectSfIntegrationCrmStatus,
    selectHsIntegrationCrmStatus,
    (sfStatus, hsStatus): Integration[] => {
        return sfStatus >= hsStatus ? ["salesforce", "hubspot"] : ["hubspot", "salesforce"];
    },
);

export const selectHasCrmLists = createSelector(selectCrmLists, (crmLists) => crmLists.length > 0);

export const selectIsFetchingCrmLists = createCrmListsSelector("isFetching");
const selectFetchCrmListsError = createCrmListsSelector("fetchingError");

const selectHasEnabledCrmList = createSelector(
    selectHsIntegrationCrmStatus,
    selectSfIntegrationCrmStatus,
    (hsStatus, sfStatus) => {
        return [hsStatus, sfStatus].some((status) => status > CrmIntegrationListStatus.NotActive);
    },
);

const selectIsCrmListHidden = createSelector(
    selectHsIntegrationCrmStatus,
    selectSfIntegrationCrmStatus,
    (hsStatus, sfStatus) => {
        const areAllCrmNotIntegrated = [hsStatus, sfStatus].every(
            (status) => status === CrmIntegrationListStatus.NotIntegrated,
        );
        const isNotAdmin = !swSettings?.user?.hasCrmAccess;
        const isFeatureDisabled = !salesSettingsHelper.isCrmListEnabled();

        return isFeatureDisabled || (isNotAdmin && areAllCrmNotIntegrated);
    },
);
